import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../services/api';

// Feature flags for future features (all disabled in BETA)
const FEATURE_FLAGS = {
  ENABLE_DOWNLOADS: false,
  ENABLE_ADVANCED_SCANNING: false,
  ENABLE_REAL_TIME_MONITORING: false,
  ENABLE_API_INTEGRATIONS: false,
  ENABLE_PDF_REPORTS: false,
};

const Cybersecurity = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Form states
  const [url, setUrl] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordLength, setPasswordLength] = useState(16);

  // Determine user level based on plan
  const getUserLevel = () => {
    const plan = user?.plan?.toLowerCase() || 'free';
    if (['professional', 'premium', 'elite', 'admin'].includes(plan)) return 'advanced';
    if (['standard'].includes(plan)) return 'intermediate';
    return 'basic';
  };

  const userLevel = getUserLevel();

  // Tool definitions by level
  const tools = {
    basic: [
      {
        id: 'url_check',
        icon: '🔍',
        title: 'Revisión de URL',
        description: 'Análisis básico de estructura y headers visibles de una URL',
        color: 'blue'
      },
      {
        id: 'password_gen',
        icon: '🔐',
        title: 'Generador de Contraseñas',
        description: 'Crea contraseñas seguras con diferentes niveles de complejidad',
        color: 'green'
      },
      {
        id: 'education',
        icon: '📚',
        title: 'Guía de Seguridad',
        description: 'Información educativa sobre riesgos comunes y buenas prácticas',
        color: 'purple'
      },
    ],
    intermediate: [
      {
        id: 'ssl_info',
        icon: '🔒',
        title: 'Información SSL',
        description: 'Análisis informativo de certificados SSL y su configuración',
        color: 'emerald'
      },
      {
        id: 'phishing_patterns',
        icon: '🎣',
        title: 'Detector de Patrones',
        description: 'Identificación de patrones comunes de phishing en URLs',
        color: 'orange'
      },
      {
        id: 'headers_info',
        icon: '📋',
        title: 'Auditoría de Headers',
        description: 'Revisión informativa de headers HTTP y buenas prácticas',
        color: 'cyan'
      },
    ],
    advanced: [
      {
        id: 'dashboard_report',
        icon: '📊',
        title: 'Panel de Reportes',
        description: 'Resumen informativo de análisis realizados en tu cuenta',
        color: 'indigo'
      },
      {
        id: 'risk_alerts',
        icon: '⚠️',
        title: 'Alertas Educativas',
        description: 'Notificaciones informativas sobre riesgos potenciales',
        color: 'amber'
      },
      {
        id: 'mitigation_guides',
        icon: '📖',
        title: 'Guías de Mitigación',
        description: 'Documentación teórica sobre técnicas de protección',
        color: 'rose'
      },
    ],
  };

  // Get available tools for user
  const getAvailableTools = () => {
    let available = [...tools.basic];
    if (userLevel === 'intermediate' || userLevel === 'advanced') {
      available = [...available, ...tools.intermediate];
    }
    if (userLevel === 'advanced') {
      available = [...available, ...tools.advanced];
    }
    return available;
  };

  const availableTools = getAvailableTools();

  // Set default active tab
  useEffect(() => {
    if (availableTools.length > 0 && !activeTab) {
      setActiveTab(availableTools[0].id);
    }
  }, [availableTools, activeTab]);

  const getLevelInfo = () => {
    const levels = {
      basic: { name: 'Básico', color: 'blue', icon: '🔵' },
      intermediate: { name: 'Intermedio', color: 'green', icon: '🟢' },
      advanced: { name: 'Avanzado', color: 'red', icon: '🔴' },
    };
    return levels[userLevel];
  };

  const levelInfo = getLevelInfo();

  // API Handlers
  const handleURLCheck = async () => {
    if (!url.trim()) {
      setError('Por favor ingresa una URL');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/security/scan/url', { url });
      setResult({ type: 'url_check', data: response.data });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al analizar URL');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordGenerate = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/security/generate/password', { 
        length: parseInt(passwordLength),
        include_uppercase: true,
        include_lowercase: true,
        include_numbers: true,
        include_symbols: true
      });
      setResult({ type: 'password_gen', data: response.data });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al generar contraseña');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordAnalyze = async () => {
    if (!password.trim()) {
      setError('Por favor ingresa una contraseña');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/security/analyze/password', { password });
      setResult({ type: 'password_analyze', data: response.data });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al analizar contraseña');
    } finally {
      setLoading(false);
    }
  };

  const handleSSLInfo = async () => {
    if (!url.trim()) {
      setError('Por favor ingresa un dominio');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/security/analyze/ssl', { domain: url });
      setResult({ type: 'ssl_info', data: response.data });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al analizar SSL');
    } finally {
      setLoading(false);
    }
  };

  const handlePhishingPatterns = async () => {
    if (!url.trim()) {
      setError('Por favor ingresa una URL');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/security/detect/phishing', { url });
      setResult({ type: 'phishing_patterns', data: response.data });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al analizar patrones');
    } finally {
      setLoading(false);
    }
  };

  const handleHeadersInfo = async () => {
    if (!url.trim()) {
      setError('Por favor ingresa una URL');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/security/audit/configuration', { url });
      setResult({ type: 'headers_info', data: response.data });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al auditar headers');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copiado al portapapeles');
  };

  // Educational content
  const educationalContent = {
    common_risks: [
      { title: 'Phishing', desc: 'Correos o sitios falsos que imitan entidades legítimas para robar datos.' },
      { title: 'Malware', desc: 'Software malicioso que puede dañar tu equipo o robar información.' },
      { title: 'Contraseñas débiles', desc: 'Claves fáciles de adivinar que comprometen tus cuentas.' },
      { title: 'Redes públicas', desc: 'WiFi abiertos donde terceros pueden interceptar tu tráfico.' },
      { title: 'Ingeniería social', desc: 'Manipulación psicológica para obtener información confidencial.' },
    ],
    best_practices: [
      'Usa contraseñas únicas de al menos 12 caracteres',
      'Activa la autenticación de dos factores (2FA)',
      'Mantén tu software actualizado',
      'No hagas clic en enlaces sospechosos',
      'Verifica la URL antes de ingresar datos',
      'Realiza copias de seguridad periódicas',
    ],
  };

  const mitigationGuides = [
    { 
      title: 'Protección contra Phishing',
      content: 'Verifica siempre la URL, busca el candado HTTPS, no compartas datos por email, y usa gestores de contraseñas.',
      level: 'Básico'
    },
    {
      title: 'Seguridad de Contraseñas',
      content: 'Mínimo 12 caracteres, combina mayúsculas, minúsculas, números y símbolos. Nunca reutilices contraseñas.',
      level: 'Básico'
    },
    {
      title: 'Configuración de Headers HTTP',
      content: 'Implementa Content-Security-Policy, X-Frame-Options, X-Content-Type-Options para proteger tu sitio.',
      level: 'Intermedio'
    },
    {
      title: 'Certificados SSL/TLS',
      content: 'Usa TLS 1.3, renueva certificados antes de expirar, configura HSTS para forzar conexiones seguras.',
      level: 'Intermedio'
    },
    {
      title: 'Monitoreo de Seguridad',
      content: 'Implementa logging, revisa accesos sospechosos, configura alertas para actividad inusual.',
      level: 'Avanzado'
    },
  ];

  // Render tool panel
  const renderToolPanel = () => {
    switch (activeTab) {
      case 'url_check':
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                ℹ️ Este análisis revisa la estructura básica de la URL y headers públicos visibles. 
                No realiza pruebas intrusivas ni accede a contenido privado.
              </p>
            </div>
            <input
              type="url"
              placeholder="https://ejemplo.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              onClick={handleURLCheck}
              disabled={loading}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Analizando...' : '🔍 Revisar URL'}
            </button>
          </div>
        );

      case 'password_gen':
        return (
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <p className="text-sm text-green-700 dark:text-green-300">
                ℹ️ Las contraseñas se generan localmente y no se almacenan en ningún servidor.
              </p>
            </div>
            
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Longitud de contraseña: {passwordLength} caracteres
              </label>
              <input
                type="range"
                min="8"
                max="64"
                value={passwordLength}
                onChange={(e) => setPasswordLength(e.target.value)}
                className="w-full"
              />
              <button
                onClick={handlePasswordGenerate}
                disabled={loading}
                className="w-full py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Generando...' : '🎲 Generar Contraseña'}
              </button>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                O analiza una contraseña existente:
              </label>
              <input
                type="password"
                placeholder="Ingresa tu contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-2"
              />
              <button
                onClick={handlePasswordAnalyze}
                disabled={loading || !password}
                className="w-full py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Analizando...' : '🔐 Analizar Fortaleza'}
              </button>
            </div>
          </div>
        );

      case 'education':
        return (
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">⚠️ Riesgos Comunes</h4>
              <div className="space-y-2">
                {educationalContent.common_risks.map((risk, i) => (
                  <div key={i} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    <p className="font-medium text-gray-900 dark:text-white">{risk.title}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{risk.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">✅ Buenas Prácticas</h4>
              <ul className="space-y-2">
                {educationalContent.best_practices.map((practice, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>{practice}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );

      case 'ssl_info':
        return (
          <div className="space-y-4">
            <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg">
              <p className="text-sm text-emerald-700 dark:text-emerald-300">
                ℹ️ Análisis informativo del certificado SSL público. No realiza pruebas de vulnerabilidades.
              </p>
            </div>
            <input
              type="text"
              placeholder="ejemplo.com (sin https://)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              onClick={handleSSLInfo}
              disabled={loading}
              className="w-full py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Analizando...' : '🔒 Ver Información SSL'}
            </button>
          </div>
        );

      case 'phishing_patterns':
        return (
          <div className="space-y-4">
            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
              <p className="text-sm text-orange-700 dark:text-orange-300">
                ℹ️ Detecta patrones comunes de phishing basados en la estructura de la URL. 
                Los resultados son orientativos y no garantizan seguridad absoluta.
              </p>
            </div>
            <input
              type="url"
              placeholder="https://url-sospechosa.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              onClick={handlePhishingPatterns}
              disabled={loading}
              className="w-full py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Analizando...' : '🎣 Analizar Patrones'}
            </button>
          </div>
        );

      case 'headers_info':
        return (
          <div className="space-y-4">
            <div className="bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg">
              <p className="text-sm text-cyan-700 dark:text-cyan-300">
                ℹ️ Revisa los headers HTTP públicos y compara con buenas prácticas de seguridad.
              </p>
            </div>
            <input
              type="url"
              placeholder="https://tu-sitio.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              onClick={handleHeadersInfo}
              disabled={loading}
              className="w-full py-3 bg-cyan-600 text-white rounded-lg font-semibold hover:bg-cyan-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Auditando...' : '📋 Revisar Headers'}
            </button>
          </div>
        );

      case 'dashboard_report':
        return (
          <div className="space-y-4">
            <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
              <p className="text-sm text-indigo-700 dark:text-indigo-300">
                ℹ️ Resumen informativo de tus análisis. Los datos son agregados y no incluyen información sensible.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-700 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">📊 Resumen de Actividad</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-indigo-600">-</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">URLs analizadas</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg text-center">
                  <p className="text-3xl font-bold text-green-600">-</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Contraseñas generadas</p>
                </div>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 text-center">
                El historial detallado estará disponible próximamente
              </p>
            </div>
          </div>
        );

      case 'risk_alerts':
        return (
          <div className="space-y-4">
            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                ℹ️ Alertas educativas basadas en tendencias generales de seguridad. No son alertas personalizadas.
              </p>
            </div>
            <div className="space-y-3">
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border-l-4 border-amber-500">
                <p className="font-medium text-gray-900 dark:text-white">🔔 Tendencia: Aumento de phishing</p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Se observa un incremento en campañas de phishing simulando servicios bancarios. 
                  Verifica siempre la URL antes de ingresar credenciales.
                </p>
              </div>
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border-l-4 border-blue-500">
                <p className="font-medium text-gray-900 dark:text-white">📢 Recomendación: Actualiza tus contraseñas</p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Es buena práctica actualizar contraseñas importantes cada 3-6 meses.
                </p>
              </div>
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border-l-4 border-green-500">
                <p className="font-medium text-gray-900 dark:text-white">✅ Buena práctica: 2FA</p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Activa la autenticación de dos factores en todas las cuentas que lo permitan.
                </p>
              </div>
            </div>
          </div>
        );

      case 'mitigation_guides':
        return (
          <div className="space-y-4">
            <div className="bg-rose-50 dark:bg-rose-900/20 p-4 rounded-lg">
              <p className="text-sm text-rose-700 dark:text-rose-300">
                ℹ️ Guías teóricas de mitigación. Para implementaciones específicas, consulta con un experto.
              </p>
            </div>
            <div className="space-y-3">
              {mitigationGuides.map((guide, i) => (
                <div key={i} className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-gray-900 dark:text-white">{guide.title}</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      guide.level === 'Básico' ? 'bg-blue-100 text-blue-700' :
                      guide.level === 'Intermedio' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {guide.level}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{guide.content}</p>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Render results
  const renderResult = () => {
    if (!result) return null;

    const { type, data } = result;

    // URL Check Result
    if (type === 'url_check') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">📋 Resultado del Análisis</h3>
          
          <div className="mb-4">
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
              data.estado_general === 'seguro' ? 'bg-green-100 text-green-800' :
              data.estado_general === 'riesgo_bajo' ? 'bg-yellow-100 text-yellow-800' :
              'bg-orange-100 text-orange-800'
            }`}>
              Estado: {data.estado_general?.replace('_', ' ')}
            </span>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Se identificaron {data.total_hallazgos || 0} observaciones informativas.
          </p>

          {data.hallazgos?.length > 0 && (
            <div className="space-y-2">
              {data.hallazgos.map((h, i) => (
                <div key={i} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-sm">
                  <p className="font-medium text-gray-800 dark:text-gray-200">{h.categoria}</p>
                  <p className="text-gray-600 dark:text-gray-300">{h.hallazgo}</p>
                  {h.recomendacion && (
                    <p className="text-blue-600 dark:text-blue-400 mt-1 text-xs">💡 {h.recomendacion}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-gray-500 mt-4">
            ⚠️ Este análisis es informativo y no garantiza la seguridad del sitio.
          </p>
        </div>
      );
    }

    // Password Generation Result
    if (type === 'password_gen') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">🔐 Contraseña Generada</h3>
          
          <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-lg mb-4 flex items-center justify-between">
            <span className="break-all">{data.password}</span>
            <button 
              onClick={() => copyToClipboard(data.password)}
              className="ml-4 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex-shrink-0"
            >
              Copiar
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
              <p className="text-gray-500 dark:text-gray-400">Fortaleza</p>
              <p className="font-medium text-green-600">{data.fortaleza?.replace('_', ' ')}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
              <p className="text-gray-500 dark:text-gray-400">Entropía</p>
              <p className="font-medium text-gray-900 dark:text-white">{data.entropia_bits} bits</p>
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-4">
            ⚠️ Guarda esta contraseña en un lugar seguro. No se almacena en nuestros servidores.
          </p>
        </div>
      );
    }

    // Password Analysis Result
    if (type === 'password_analyze') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">🔐 Análisis de Contraseña</h3>
          
          <div className="text-center mb-4">
            <div className="text-4xl font-bold text-gray-900 dark:text-white">{data.puntuacion}/100</div>
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${
              data.nivel === 'excelente' || data.nivel === 'buena' ? 'bg-green-100 text-green-800' :
              data.nivel === 'moderada' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {data.nivel?.replace('_', ' ')}
            </span>
          </div>

          {data.feedback?.length > 0 && (
            <div className="space-y-1 text-sm">
              {data.feedback.map((f, i) => (
                <p key={i} className="text-gray-600 dark:text-gray-300">• {f}</p>
              ))}
            </div>
          )}

          {data.recomendacion && (
            <p className="text-blue-600 dark:text-blue-400 mt-3 text-sm">💡 {data.recomendacion}</p>
          )}
        </div>
      );
    }

    // SSL Info Result
    if (type === 'ssl_info') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">🔒 Información del Certificado</h3>
          
          <div className="mb-4">
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
              data.estado === 'seguro' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
            }`}>
              Estado: {data.estado}
            </span>
          </div>

          {data.certificado && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                <p className="text-gray-500">Emisor</p>
                <p className="font-medium text-gray-900 dark:text-white">{data.certificado.emisor}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                <p className="text-gray-500">Válido hasta</p>
                <p className="font-medium text-gray-900 dark:text-white">{data.certificado.valido_hasta}</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                <p className="text-gray-500">Días restantes</p>
                <p className="font-medium text-gray-900 dark:text-white">{data.certificado.dias_restantes}</p>
              </div>
              {data.cifrado && (
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <p className="text-gray-500">Cifrado</p>
                  <p className="font-medium text-gray-900 dark:text-white">{data.cifrado.nivel} ({data.cifrado.bits} bits)</p>
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-gray-500 mt-4">
            ⚠️ Información pública del certificado. No constituye una auditoría completa.
          </p>
        </div>
      );
    }

    // Phishing Patterns Result
    if (type === 'phishing_patterns') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">🎣 Análisis de Patrones</h3>
          
          <div className="text-center mb-4">
            <div className="text-4xl font-bold text-gray-900 dark:text-white">{data.probabilidad_phishing || 0}%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Indicadores de phishing detectados</p>
          </div>

          <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">{data.explicacion}</p>

          {data.indicadores_detectados?.length > 0 && (
            <div>
              <p className="font-medium text-gray-900 dark:text-white mb-2">Indicadores encontrados:</p>
              <ul className="space-y-1 text-sm">
                {data.indicadores_detectados.map((ind, i) => (
                  <li key={i} className="text-orange-600 dark:text-orange-400">⚠️ {ind}</li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-xs text-gray-500 mt-4">
            ⚠️ Este análisis es orientativo. Ante dudas, no ingreses datos sensibles.
          </p>
        </div>
      );
    }

    // Headers Info Result
    if (type === 'headers_info') {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4">📋 Auditoría de Headers</h3>
          
          <div className="text-center mb-4">
            <div className="text-4xl font-bold text-indigo-600">{data.calificacion || '-'}</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Puntuación: {data.puntuacion || 0}/100</p>
          </div>

          {data.resumen && (
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg mb-4 text-sm">
              <p>✅ Configurados: {data.resumen.headers_configurados}</p>
              <p>❌ Faltantes: {data.resumen.headers_faltantes}</p>
            </div>
          )}

          {data.resultados?.slice(0, 5).map((r, i) => (
            <div key={i} className={`p-3 rounded-lg mb-2 ${r.estado === 'configurado' ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm text-gray-900 dark:text-white">{r.header}</span>
                <span>{r.estado === 'configurado' ? '✅' : '❌'}</span>
              </div>
            </div>
          ))}

          <p className="text-xs text-gray-500 mt-4">
            ⚠️ Recomendaciones informativas. Consulta con un desarrollador para implementarlas.
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="font-display bg-gray-50 dark:bg-background-dark min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Header />
        
        <main className="py-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              🛡️ Centro de Ciberseguridad
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Herramientas educativas e informativas para tu seguridad digital
            </p>
            
            {/* User Level Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-full shadow-sm">
              <span>{levelInfo.icon}</span>
              <span className="font-medium text-gray-900 dark:text-white">Nivel {levelInfo.name}</span>
              <span className="text-gray-500 dark:text-gray-400">•</span>
              <span className="text-gray-600 dark:text-gray-300 text-sm">Plan {user?.plan}</span>
            </div>
          </div>

          {/* Disclaimer Banner */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4 mb-8">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="font-semibold text-amber-800 dark:text-amber-300">Uso Educativo e Informativo</h3>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Este módulo proporciona información orientativa sobre seguridad. No realiza acciones intrusivas, 
                  no almacena datos sensibles y no reemplaza una auditoría profesional de seguridad.
                </p>
              </div>
            </div>
          </div>

          {/* Tools Grid */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Herramientas Disponibles
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {availableTools.map((tool) => (
                <button
                  key={tool.id}
                  onClick={() => { setActiveTab(tool.id); setResult(null); setError(''); }}
                  className={`p-4 rounded-xl text-left transition-all ${
                    activeTab === tool.id
                      ? `bg-${tool.color}-100 dark:bg-${tool.color}-900/30 border-2 border-${tool.color}-500`
                      : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <span className="text-2xl block mb-2">{tool.icon}</span>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">{tool.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{tool.description}</p>
                </button>
              ))}
            </div>

            {/* Locked Tools Preview */}
            {userLevel !== 'advanced' && (
              <div className="mt-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  🔒 Herramientas disponibles en planes superiores:
                </p>
                <div className="flex flex-wrap gap-2">
                  {userLevel === 'basic' && tools.intermediate.map((tool) => (
                    <span key={tool.id} className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs text-gray-500">
                      {tool.icon} {tool.title}
                    </span>
                  ))}
                  {tools.advanced.map((tool) => (
                    <span key={tool.id} className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs text-gray-500">
                      {tool.icon} {tool.title}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Tool Panel */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4">
                {availableTools.find(t => t.id === activeTab)?.icon} {availableTools.find(t => t.id === activeTab)?.title}
              </h3>
              
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {renderToolPanel()}
            </div>

            {/* Results Panel */}
            <div>
              {loading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-12 shadow-sm border border-gray-100 dark:border-gray-700 text-center">
                  <div className="animate-pulse text-4xl mb-4">🔄</div>
                  <p className="text-gray-600 dark:text-gray-300">Procesando...</p>
                </div>
              ) : result ? (
                renderResult()
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-12 shadow-sm border border-gray-100 dark:border-gray-700 text-center">
                  <div className="text-4xl mb-4">🛡️</div>
                  <p className="text-gray-600 dark:text-gray-300">
                    Selecciona una herramienta para comenzar
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Level Info */}
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <div className={`p-4 rounded-xl border-2 ${userLevel === 'basic' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span>🔵</span>
                <span className="font-semibold text-gray-900 dark:text-white">Nivel Básico</span>
                {userLevel === 'basic' && <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">Tu nivel</span>}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Revisión de URLs, generador de contraseñas, guías educativas
              </p>
            </div>
            
            <div className={`p-4 rounded-xl border-2 ${userLevel === 'intermediate' ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span>🟢</span>
                <span className="font-semibold text-gray-900 dark:text-white">Nivel Intermedio</span>
                {userLevel === 'intermediate' && <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Tu nivel</span>}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                + Análisis SSL, detector de patrones, auditoría de headers
              </p>
            </div>
            
            <div className={`p-4 rounded-xl border-2 ${userLevel === 'advanced' ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span>🔴</span>
                <span className="font-semibold text-gray-900 dark:text-white">Nivel Avanzado</span>
                {userLevel === 'advanced' && <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">Tu nivel</span>}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                + Panel de reportes, alertas educativas, guías de mitigación
              </p>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    </div>
  );
};

export default Cybersecurity;
